/**
 * AppErrorBoundary — the last line under every route.
 *
 * Without one, a render-time throw anywhere in the tree unmounts React and
 * leaves a blank white page: no message, no way back, and nothing in front of
 * the visitor that says what happened. This catches that and shows the same
 * error page the routes already use for a failed load, so a crash and a failed
 * fetch look the same to whoever is reading the screen.
 *
 * It only catches RENDER errors — React boundaries never see a rejected promise
 * from an event handler or an effect. Those are handled where they happen; this
 * is for the ones nobody predicted.
 */

import React from 'react';
import PublicErrorPage from './PublicErrorPage';

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false };
  }

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error, info) {
    // Kept to the console rather than swallowed: without it the stack is lost
    // and the only evidence left is the error page itself.
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info?.componentStack);
  }

  render() {
    if (!this.state.crashed) return this.props.children;

    // Clearing the flag re-renders the subtree. That is enough for a transient
    // fault; a permanent one lands straight back here rather than looping,
    // because the retry is the visitor pressing a button, not an interval.
    return (
      <PublicErrorPage
        error={{ status: 0 }}
        onRetry={() => this.setState({ crashed: false })}
      />
    );
  }
}
